//newpage.
import TimeframeChart from "../components/TimeFrameChart/TimeframeChart";
//import TimeframeChart from "./TimeframeChart"; // assuming this path
import React, { useRef, useEffect, useState } from "react";

function formatUSD(value) {
  if (!value) return "—";
  const v = Number(value);
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(0)}`;
}

export default function TopMovers({
  data,
  rows5m,
  rows15m,
  rows1h,
  rows24h,
  setRows5m,
  setRows15m,
  setRows1h,
  setRows24h,
  selectedExchanges,
  toggleExchange,
  forceRefresh,
  loading 
}) {
  /* ---------------- FILTERED DATA ---------------- */
  const filterByExchange = (rows = []) =>
    rows.filter(
      (c) =>
        Array.isArray(c.exchanges) &&
        c.exchanges.some((e) => selectedExchanges.includes(e))
    );

  const filteredTop5m = filterByExchange(data.top5m);
  const filteredTop15m = filterByExchange(data.top15m);
  const filteredTop1h = filterByExchange(data.top1h);
  const filteredTop24h = filterByExchange(data.top24h);

  /* ---------------- RETURN JSX ---------------- */
  return (
    <div className="page-section">
      {/* Page Header */}
      <header className="page-header">
        <h2>Top Movers</h2>
        <p className="page-subtitle">Track the biggest gainers across multiple timeframes</p>
      </header>

      {/* Section: Top Movers 5 Minutes */}
<Section
  title="Top Movers — 5 Minutes"
  timeframe="5m"
  rows={filteredTop5m}
  limit={rows5m}
  onLimitChange={setRows5m}
>
  <div
    className="controls-row"
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "10px",
      gap: "10px",
      flexWrap: "wrap" // ensures responsiveness on small screens
    }}
  >
    {/* Exchange toggles */}
    <div className="exchange-toggles" style={{ display: "flex", gap: "10px" }}>
      {["MEXC", "HyperLiquid", "Bitunix", "Bybit"].map((ex) => (
        <label key={ex} className="switch">
          <input
            type="checkbox"
            checked={selectedExchanges.includes(ex)}
            onChange={() => toggleExchange(ex)}
          />
          <span className="slider"></span>
          <span className="switch-label">{ex}</span>
        </label>
      ))}
    </div>

    {/* Refresh button */}
    <button
      onClick={forceRefresh}
      disabled={loading}
      className="refresh-btn"
    >
      🔄 Refresh
    </button>
  </div>
</Section>


      {/* Section: Top Movers 15 Minutes */}
      <Section
        title="Top Movers — 15 Minutes"
        timeframe="15m"
        rows={filteredTop15m}
        limit={rows15m}
        onLimitChange={setRows15m}
      />

      {/* Section: Top Movers 1 Hour */}
      <Section
        title="Top Movers — 1 Hour"
        timeframe="1h"
        rows={filteredTop1h}
        limit={rows1h}
        onLimitChange={setRows1h}
      />

      {/* Section: Top Movers 24 Hours */}
      <Section
        title="Top Movers — 24 Hours (updated on the hour)"
        timeframe="24h"
        rows={filteredTop24h}
        limit={rows24h}
        onLimitChange={setRows24h}
      />
    </div>
  );
}

/* ---------------------------------------
   Section Component
--------------------------------------- */



function Section({ title, timeframe, rows = [], limit, onLimitChange, children }) {
  const visibleRows = rows.slice(0, limit);

  // --- NEW: measure table body height ---
  const tableBodyRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(200);

  useEffect(() => {
    if (tableBodyRef.current) {
      setTableHeight(tableBodyRef.current.offsetHeight);
    }
  }, [visibleRows]);

  return (
    <section className="timeframe-section">

      {/* Children = exchange filters + refresh button (5m only) */}
      {children}

      {/* Header + dropdown */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "8px"
        }}
      >
        <h2 style={{ marginBottom: 0 }}>{title}</h2>

        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          style={{
            background: "var(--bg-card)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: "6px",
            padding: "4px 8px",
            fontSize: "13px",
            marginTop: "25px",
            cursor: "pointer"
          }}
        >
          <option value={5}>5 rows</option>
          <option value={10}>10 rows</option>
          <option value={15}>15 rows</option>
          <option value={25}>25 rows</option>
        </select>
      </div>

      {/* Table + Chart side by side */}
      <div className="timeframe-content">
        <div className="table-wrapper">
          {visibleRows.length === 0 ? (
            <p className="loading">
              {title.includes("24 Hours")
                ? "⏳ Gathering 24h data — check back soon"
                : "No movers yet"}
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Price</th>
                  <th>Change</th>
                  <th>Market Cap</th>
                  <th>24h Volume</th>
                </tr>
              </thead>

              {/* --- NEW: ref on tbody so we measure ONLY the rows --- */}
              <tbody ref={tableBodyRef}>
                {visibleRows.map((c) => {
                  const volume = Number(c.volume24h ?? 0);
                  const marketCap = Number(c.marketCap ?? 0);
                  const isVolumeSpike = marketCap > 0 && volume > marketCap;

                  return (
                    <tr
                      key={`${c.coin}-${c.symbol}`}
                      className={isVolumeSpike ? "volume-spike" : ""}
                    >
                      <td>
                        {c.symbol}{" "}
                        <a
                          href={`https://www.coinglass.com/pro/futures/LiquidationHeatMap?coin=${c.symbol.toUpperCase()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            marginLeft: "6px",
                            fontSize: "12px",
                            opacity: 0.7
                          }}
                        >
                          📊
                        </a>
                      </td>
                      <td>${Number(c.price).toFixed(6)}</td>
                      <td className={c.change >= 0 ? "change up" : "change down"}>
                        {c.change >= 0 ? "+" : ""}
                        {c.change.toFixed(2)}%
                      </td>
                      <td>{formatUSD(c.marketCap)}</td>
                      <td>{formatUSD(c.volume24h)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Chart on the right — now receives dynamic height */}
        <TimeframeChart
          coins={visibleRows}
          selectedTimeframe={timeframe}
          height={tableHeight}
        />
      </div>

    </section>
  );
}

